import { MatDialogRef, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AlertDialogComponent } from './alert-dialog/alert-dialog.component';
import { SelectDialogComponent, SelectDialogAction } from './select-dialog/select-dialog.component';
import { TranslateService } from '@ngx-translate/core';

export interface DialogAction {
	label?: string;
	labelKey?: string;
	action?: () => void | Promise<void>;
	styleClass?: string;
	highlighted?: boolean;
	disabled?: boolean;
}

export interface SelectDialogOptions {
	title?: string;
	titleKey?: string;
	titleParams?: Record<string, any>;
	message?: string;
	messageKey?: string;
	messageParams?: Record<string, any>;
	actions: DialogAction[];
	showCancel?: boolean; // default true
	cancelLabel?: string;
	cancelLabelKey?: string;
	cancelHighlighted?: boolean; // default true
	config?: any;
}

export interface ConfirmDialogOptions {
	title?: string;
	titleKey?: string;
	message?: string;
	messageKey?: string;
	messageParams?: Record<string, any>;
	config?: any;
	onConfirm?: () => void | Promise<void>;
	onCancel?: () => void | Promise<void>;
}

@Injectable({
	providedIn: 'root',
})
export class DialogsService {
	constructor(private dialog: MatDialog, private translateService: TranslateService) {}

	public alert(title: string, message: string, config?: any): Observable<boolean> {
		let dialogRef: MatDialogRef<AlertDialogComponent>;

		const dialogConfig = new MatDialogConfig();

		dialogConfig.panelClass = 'alert';
		dialogConfig.disableClose = true;
		dialogConfig.autoFocus = true;
		dialogConfig.data = {
			title: title,
			message: message,
			config: config,
		};

		dialogRef = this.dialog.open(AlertDialogComponent, dialogConfig);

		return dialogRef.afterClosed();
	}

	public async select(options: SelectDialogOptions): Promise<any> {
		// Handle translation
		const resolvedTitle =
			options.title ||
			(options.titleKey
				? this.translateService.instant(options.titleKey, options.titleParams || options.messageParams)
				: '');
		const resolvedMessage =
			options.message ||
			(options.messageKey ? this.translateService.instant(options.messageKey, options.messageParams) : '');

		const showCancel = options.showCancel !== false; // default true
		const cancelLabel =
			options.cancelLabel ||
			(options.cancelLabelKey
				? this.translateService.instant(options.cancelLabelKey)
				: this.translateService.instant('cancel'));

		// Resolve action labels
		const resolvedActions: SelectDialogAction[] = options.actions.map((action, index) => ({
			label: action.label || (action.labelKey ? this.translateService.instant(action.labelKey) : ''),
			value: index,
			styleClass: action.styleClass,
			highlighted: action.highlighted,
			disabled: action.disabled,
		}));

		const dialogConfig = new MatDialogConfig();
		dialogConfig.panelClass = 'select';
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.data = {
			title: resolvedTitle,
			message: resolvedMessage,
			actions: resolvedActions,
			showCancel,
			cancelLabel,
			cancelHighlighted: options.cancelHighlighted ?? true,
		};

		const dialogRef = this.dialog.open(SelectDialogComponent, dialogConfig);
		const result = await dialogRef.afterClosed().toPromise();

		// If result is null, user cancelled
		if (result === null || result === undefined) {
			return null;
		}

		// Execute the action callback if provided
		const selectedAction = options.actions[result];
		if (selectedAction?.action) {
			await selectedAction.action();
		}

		return result;
	}

	public async confirm(options: ConfirmDialogOptions): Promise<boolean> {
		// Use select() with two actions
		const result = await this.select({
			title: options.title,
			titleKey: options.titleKey,
			message: options.message,
			messageKey: options.messageKey,
			messageParams: options.messageParams,
			actions: [
				{
					labelKey: 'yes',
					action: options.onConfirm,
					styleClass: 'outline-button',
				},
			],
			showCancel: true,
			cancelLabelKey: 'no',
			config: options.config,
		});

		// Handle onCancel callback
		if (result === null && options.onCancel) {
			await options.onCancel();
		}

		return result !== null;
	}
}
